/**
 * Security utility for safe error message handling
 * Maps internal database errors to user-friendly messages
 * Prevents information leakage about database structure
 */

export const getSafeErrorMessage = (error: any): string => {
  // Handle null/undefined errors
  if (!error) {
    return "An unexpected error occurred. Please try again.";
  }

  const errorMessage = error.message || "";
  const errorCode = error.code || "";

  // PostgreSQL error codes
  const postgresErrorMap: Record<string, string> = {
    "23505": "This record already exists. Please check your input.",
    "23503": "Invalid reference. Please check your input.",
    "23502": "Required field is missing. Please fill in all required fields.",
    "23514": "Invalid input value. Please check your data.",
    "PGRST116": "The result contains multiple rows. Please contact support.",
  };

  // Check for PostgreSQL error codes
  if (errorCode && postgresErrorMap[errorCode]) {
    return postgresErrorMap[errorCode];
  }

  // Handle common authentication errors with specific messages
  if (errorMessage.toLowerCase().includes("invalid login credentials")) {
    return "Invalid email or password. Please try again.";
  }

  if (errorMessage.toLowerCase().includes("already registered")) {
    return "This email is already registered. Please login instead.";
  }

  // Handle RLS policy violations - don't reveal policy details
  if (errorMessage.toLowerCase().includes("row-level security") || 
      errorMessage.toLowerCase().includes("rls policy") ||
      errorMessage.toLowerCase().includes("policy")) {
    return "You don't have permission to perform this action.";
  }

  // Handle constraint violations without revealing constraint names
  if (errorMessage.toLowerCase().includes("duplicate key") ||
      errorMessage.toLowerCase().includes("unique constraint")) {
    return "This record already exists.";
  }

  if (errorMessage.toLowerCase().includes("foreign key") ||
      errorMessage.toLowerCase().includes("violates")) {
    return "Invalid data. Please check your input.";
  }

  // Handle validation errors
  if (errorMessage.toLowerCase().includes("check constraint") ||
      errorMessage.toLowerCase().includes("length")) {
    return "Invalid input. Please check the length and format of your data.";
  }

  // Handle network/connection errors
  if (errorMessage.toLowerCase().includes("network") ||
      errorMessage.toLowerCase().includes("fetch") ||
      errorMessage.toLowerCase().includes("connection")) {
    return "Connection error. Please check your internet connection and try again.";
  }

  // Default safe message for unknown errors
  return "An error occurred. Please try again later.";
};
